import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import { getCycleFromCycleName, getPresenceMode, CYCLES } from '../utils/cycle.helpers';
import { useAuth } from '../context/AuthContext';

const STATUSES = [
  { v: 'PRESENT', l: 'P', title: 'Présent',  cls: 'present' },
  { v: 'ABSENT',  l: 'A', title: 'Absent',   cls: 'absent'  },
  { v: 'RETARD',  l: 'R', title: 'Retard',   cls: 'retard'  },
  { v: 'EXCUSE',  l: 'E', title: 'Excusé',   cls: 'excuse'  },
];

const MOTIFS = ['Maladie', 'Rendez-vous médical', 'Deuil', 'Convocation', 'Raison familiale', 'Transport', 'Non justifié', 'Autre'];

function today() { return new Date().toISOString().slice(0, 10); }

// Graphique donut SVG simple
function DonutChart({ data }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 20 }}>Aucune donnée</div>;

  let offset = 0;
  const R = 60, cx = 80, cy = 80, circumference = 2 * Math.PI * R;
  const segments = data.map((d) => {
    const pct = d.value / total;
    const dash = pct * circumference;
    const seg = { ...d, dash, offset, pct };
    offset += dash;
    return seg;
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <svg width="160" height="160" viewBox="0 0 160 160">
        {segments.map((s, i) => (
          <circle key={i} cx={cx} cy={cy} r={R}
            fill="none" stroke={s.color} strokeWidth="28"
            strokeDasharray={`${s.dash} ${circumference - s.dash}`}
            strokeDashoffset={-s.offset + circumference / 4}
            transform="rotate(-90 80 80)"
          />
        ))}
        <circle cx={cx} cy={cy} r={R - 14} fill="var(--surface)" />
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize="18" fontWeight="bold" fill="var(--text)">{total}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize="10" fill="var(--muted)">élèves</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {segments.map((s) => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem' }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
            <span>{s.label}</span>
            <strong style={{ marginLeft: 'auto', minWidth: 28 }}>{s.value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}


function DateNav({ value, onChange }) {
  const dateRef = useRef(null);
  function shift(days) {
    const d = new Date(value + 'T12:00:00');
    d.setDate(d.getDate() + days);
    onChange(d.toISOString().slice(0, 10));
  }
  const label = new Date(value + 'T12:00:00').toLocaleDateString('fr-FR', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
  });
  const isToday = value === new Date().toISOString().slice(0, 10);
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <button className="btn-ghost btn-sm" onClick={() => shift(-1)} style={{ padding: '4px 10px', fontWeight: 700 }}>&#8249;</button>
      <div style={{ position: 'relative', minWidth: 200 }}>
        <span onClick={() => dateRef.current?.showPicker?.() || dateRef.current?.click()}
          style={{ fontWeight: 600, fontSize: '0.88rem', textAlign: 'center', textTransform: 'capitalize',
            cursor: 'pointer', display: 'block', padding: '4px 8px', borderRadius: 6,
            border: '1px solid var(--line)', background: isToday ? 'var(--primary-soft, #e8f4f8)' : 'var(--surface)',
            color: isToday ? 'var(--primary, #1a6b8a)' : 'inherit', userSelect: 'none' }}
          title="Cliquer pour choisir une date">
          &#128197; {label}
        </span>
        <input ref={dateRef} type="date" value={value} onChange={(e) => onChange(e.target.value)}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: -1 }} />
      </div>
      <button className="btn-ghost btn-sm" onClick={() => shift(1)} style={{ padding: '4px 10px', fontWeight: 700 }}>&#8250;</button>
      {!isToday && <button className="btn-ghost btn-sm" onClick={() => onChange(new Date().toISOString().slice(0, 10))} style={{ fontSize: '0.72rem' }}>Aujourd'hui</button>}
    </div>
  );
}
export default function Attendance() {
  const { user } = useAuth();
  const [tab, setTab]           = useState('dashboard');
  const [classes, setClasses]   = useState([]);
  const [classId, setClassId]   = useState('');
  const [date, setDate]         = useState(today());
  const [rows, setRows]         = useState([]);
  const [allAbsents, setAllAbsents] = useState([]);
  const [classStats, setClassStats] = useState([]);
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [presenceMode, setPresenceMode] = useState('PAR_COURS'); // PAR_COURS | DEMI_JOURNEE
  const [notifyResult, setNotifyResult] = useState(null);
  const [addModal, setAddModal] = useState(null);

  useEffect(() => {
    const roles = user?.roles || [];
    const isEnseignant = roles.includes('ENSEIGNANT') &&
      !roles.includes('ADMINISTRATEUR') && !roles.includes('PROVISEUR') &&
      !roles.includes('CENSEUR') && !roles.includes('SECRETAIRE') && !roles.includes('COMPTABLE');

    Promise.all([
      api.get('/catalog/classes'),
      isEnseignant ? api.get('/catalog/teachers') : Promise.resolve([]),
      isEnseignant ? api.get('/timetable') : Promise.resolve([]),
    ]).then(([allClasses, allTeachers, sessions]) => {
      if (!isEnseignant) {
        setClasses(allClasses);
        if (allClasses[0]) setClassId(allClasses[0].id);
        return;
      }
      const ln = (user?.lastName || '').trim().toLowerCase();
      const fn = (user?.firstName || '').trim().toLowerCase();
      const me = (allTeachers || []).find((t) =>
        (t.lastName || '').trim().toLowerCase() === ln &&
        (t.firstName || '').trim().toLowerCase() === fn
      );
      if (!me) {
        // Enseignant non trouvé dans le catalogue — fallback sans restriction
        setClasses(allClasses);
        if (allClasses[0]) setClassId(allClasses[0].id);
        return;
      }
      const myClassIds = new Set(
        (sessions || [])
          .filter((s) => s.teacher?.id === me.id || s.teacherId === me.id)
          .map((s) => s.class?.id || s.classId)
          .filter(Boolean)
      );
      const myClasses = allClasses.filter((c) => myClassIds.has(c.id));
      setClasses(myClasses);
      if (myClasses[0]) setClassId(myClasses[0].id);
    }).catch((e) => setError(e.message));
  }, [user]);

  // Charger absents du jour et stats par classe
  function loadDashboard() {
    // Les absences du jour - filtre strict sur la date
    api.get(`/registry/schoolLife`).then((d) => {
      const records = (Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data }));
      setAllAbsents(records.filter((r) => r.eventDate === date && r.type === 'Absence'));
    }).catch(() => {});
    // Charger les appels du jour depuis le registry
    api.get('/registry/presences-eleves').then((d) => {
      const all = (Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data }));
      const today = all.filter((r) => r.date === date);
      const stats = classes.map((c) => {
        const rec = today.find((r) => r.classId === c.id);
        return { class: c, rows: rec?.entries || [], recorded: !!rec };
      });
      setClassStats(stats);
    }).catch(() => setClassStats(classes.map((c) => ({ class: c, rows: [], recorded: false }))));
  }

  useEffect(() => { if (classes.length) loadDashboard(); }, [date, classes]);

  function loadRoster() {
    if (!classId || !date) return;
    // Détecter le mode de présence selon le cycle de la classe
    const cls = classes.find((c) => c.id === classId);
    const cycle = cls?.level?.cycle?.name ? getCycleFromCycleName(cls.level.cycle.name) : null;
    setPresenceMode(getPresenceMode(cycle));
    setSaved(false); setNotifyResult(null);
    // Chercher l'appel du jour dans le registry
    api.get('/registry/presences-eleves').then((d) => {
      const all = (Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data }));
      const existing = all.find((r) => r.classId === classId && r.date === date);
      if (existing?.entries?.length) {
        setRows(existing.entries);
      } else {
        // Initialiser depuis la liste des élèves
        api.get('/students').then((students) => {
          const roster = (students || [])
            .filter((s) => (s.enrollments || []).some((en) => en.classId === classId && en.status === 'ACTIVE'))
            .map((s) => ({ studentId: s.id, matricule: s.matricule, name: `${s.lastName} ${s.firstName}`, status: 'PRESENT', note: '' }));
          setRows(roster);
        }).catch((e) => setError(e.message));
      }
    }).catch(() => {
      api.get('/students').then((students) => {
        const roster = (students || [])
          .filter((s) => (s.enrollments || []).some((en) => en.classId === classId))
          .map((s) => ({ studentId: s.id, matricule: s.matricule, name: `${s.lastName} ${s.firstName}`, status: 'PRESENT', note: '' }));
        setRows(roster);
      }).catch((e) => setError(e.message));
    });
  }
  useEffect(loadRoster, [classId, date]);

  const counts = useMemo(() => {
    const c = { PRESENT: 0, ABSENT: 0, RETARD: 0, EXCUSE: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  // Stats globales du jour
  const globalStats = useMemo(() => {
    const c = { PRESENT: 0, ABSENT: 0, RETARD: 0, EXCUSE: 0, total: 0 };
    classStats.forEach((cs) => {
      cs.rows.forEach((r) => {
        c[r.status] = (c[r.status] || 0) + 1;
        c.total++;
      });
    });
    return c;
  }, [classStats]);

  function setStatus(studentId, status) {
    setSaved(false);
    setRows((rs) => rs.map((r) => r.studentId === studentId ? { ...r, status } : r));
  }
  function allPresent() {
    setSaved(false);
    setRows((rs) => rs.map((r) => ({ ...r, status: 'PRESENT' })));
  }

  async function save() {
    setError(''); setSaving(true);
    try {
      // Sauvegarder tout l'appel dans le registry
      await api.post('/registry/presences-eleves', {
        data: { classId, date, entries: rows.map((r) => ({ studentId: r.studentId, status: r.status, note: r.note })) },
        label: `Appel ${date} - ${classes.find((c) => c.id === classId)?.name || classId}`,
        searchText: `${classId} ${date}`,
        refDate: date,
      });
      setSaved(true);
      loadDashboard();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  async function notifyAbsents() {
    try {
      const absents = rows.filter((r) => r.status === 'ABSENT');
      const r = await api.post('/communication/send/class', {
        classId, channel: 'SMS',
        body: `Votre enfant a été absent le ${new Date(date).toLocaleDateString('fr-FR')}. Veuillez contacter l'établissement.`,
      });
      setNotifyResult({ sent: r?.sent || absents.length, total: absents.length });
    } catch (err) { setError(err.message); }
  }

  const dateLabel = new Date(date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const donutData = [
    { label: 'Présents',  value: globalStats.PRESENT, color: '#4caf50' },
    { label: 'Absents',   value: globalStats.ABSENT,  color: '#f44336' },
    { label: 'Retards',   value: globalStats.RETARD,  color: '#ff9800' },
    { label: 'Excusés',   value: globalStats.EXCUSE,  color: '#2196f3' },
  ].filter((d) => d.value > 0);

  // Vérifier quelles classes n'ont pas encore fait l'appel
  const classesEnAttente = classStats.filter((cs) => cs.rows.length === 0 || cs.rows.every((r) => r.status === 'PRESENT' && !r.recorded));

  return (
    <>
      <div className="tabs" style={{ marginBottom: 4 }}>
        <button className={`tab ${tab === 'dashboard' ? 'active' : ''}`} onClick={() => setTab('dashboard')}>Tableau de bord</button>
        <button className={`tab ${tab === 'appel' ? 'active' : ''}`} onClick={() => setTab('appel')}>Feuille d'appel</button>
        <button className={`tab ${tab === 'historique' ? 'active' : ''}`} onClick={() => setTab('historique')}>Historique</button>
      </div>

      {tab === 'dashboard' && (
        <>
          {/* En-tête date */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <h2 style={{ margin: 0, textTransform: 'capitalize' }}>Tableau de bord — {dateLabel}</h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
<DateNav value={date} onChange={setDate} />
              <button className="btn btn-sm" onClick={() => setAddModal({})}>+ Saisir une absence</button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, alignItems: 'start' }}>
            {/* Tableau vue d'ensemble par classe */}
            <div className="card">
              <div className="card-head"><h2>Vue d'ensemble {date.slice(0, 4)}-{Number(date.slice(0, 4)) + 1}</h2></div>
              <div className="card-body" style={{ padding: 0 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--navy, #1a3a5c)', color: 'white' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Classe</th>
                      <th style={{ padding: '8px 8px', textAlign: 'center' }}>Élèves</th>
                      <th style={{ padding: '8px 8px', textAlign: 'center', color: '#f44336' }}>Absences</th>
                      <th style={{ padding: '8px 8px', textAlign: 'center', color: '#ff9800' }}>Retards</th>
                      <th style={{ padding: '8px 8px', textAlign: 'center', color: '#2196f3' }}>Excusés</th>
                      <th style={{ padding: '8px 8px', textAlign: 'center' }}>Statut appel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classStats.map((cs, i) => {
                      const absent = cs.rows.filter((r) => r.status === 'ABSENT').length;
                      const retard = cs.rows.filter((r) => r.status === 'RETARD').length;
                      const excuse = cs.rows.filter((r) => r.status === 'EXCUSE').length;
                      const done = cs.rows.length > 0;
                      return (
                        <tr key={cs.class.id} style={{ background: i % 2 === 0 ? 'var(--surface)' : 'var(--surface-soft, #f8f9fa)', cursor: 'pointer' }}
                          onClick={() => { setClassId(cs.class.id); setTab('appel'); }}>
                          <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                            <span style={{ marginRight: 6 }}>🏫</span>{cs.class.name}
                          </td>
                          <td style={{ padding: '8px 8px', textAlign: 'center' }}>{cs.rows.length || '—'}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'center', color: absent > 0 ? '#f44336' : 'var(--muted)', fontWeight: absent > 0 ? 700 : 400 }}>{absent || 0}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'center', color: retard > 0 ? '#ff9800' : 'var(--muted)', fontWeight: retard > 0 ? 700 : 400 }}>{retard || 0}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'center', color: excuse > 0 ? '#2196f3' : 'var(--muted)' }}>{excuse || 0}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                            {done
                              ? <span className="badge green" style={{ fontSize: '0.7rem' }}>✓ Effectué</span>
                              : <span className="badge" style={{ fontSize: '0.7rem', background: '#ff9800', color: 'white' }}>En attente</span>}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Ligne total */}
                    <tr style={{ background: 'var(--navy, #1a3a5c)', color: 'white', fontWeight: 700 }}>
                      <td style={{ padding: '8px 12px' }}>Total</td>
                      <td style={{ padding: '8px 8px', textAlign: 'center' }}>{globalStats.total}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'center', color: '#ff8a80' }}>{globalStats.ABSENT}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'center', color: '#ffcc80' }}>{globalStats.RETARD}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'center', color: '#82b1ff' }}>{globalStats.EXCUSE}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'center' }}>—</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Panneau droit */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Donut */}
              <div className="card">
                <div className="card-head"><h2>Feuilles d'appel du jour</h2></div>
                <div className="card-body" style={{ padding: 16 }}>
                  <DonutChart data={donutData} />
                </div>
              </div>

              {/* Classes en attente */}
              {classesEnAttente.length > 0 && (
                <div className="card">
                  <div className="card-body" style={{ padding: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: '0.85rem', color: 'var(--muted)' }}>
                      EN ATTENTE DE SAISIE
                    </div>
                    {classesEnAttente.map((cs) => (
                      <button key={cs.class.id} className="btn-ghost" style={{ width: '100%', marginBottom: 4, textAlign: 'left', fontSize: '0.82rem', padding: '6px 10px' }}
                        onClick={() => { setClassId(cs.class.id); setTab('appel'); }}>
                        🏫 {cs.class.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Absents du jour */}
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-head">
              <h2>Élèves absents aujourd'hui</h2>
              <span className="badge red">{allAbsents.length}</span>
            </div>
            <div className="card-body">
              {allAbsents.length === 0 ? <div className="empty">Aucune absence enregistrée.</div> : (
                <div className="table-wrap"><table>
                  <thead>
                    <tr><th>Du</th><th>Au</th><th>Élève</th><th>Classe</th><th>Cours manqués</th><th>Motif</th><th>Commentaire</th><th></th></tr>
                  </thead>
                  <tbody>{allAbsents.map((a, i) => (
                    <tr key={i}>
                      <td className="mono">{new Date(a.date || date).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                      <td className="mono">{new Date(a.date || date).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                      <td><b>{a.lastName} {a.firstName}</b></td>
                      <td>{a.className || '—'}</td>
                      <td className="num">{a.coursManques || 0}</td>
                      <td>{a.note || '—'}</td>
                      <td>{a.commentaire || '—'}</td>
                      <td><button className="btn-ghost btn-sm" onClick={() => setAddModal(a)}>Modifier</button></td>
                    </tr>
                  ))}</tbody>
                </table></div>
              )}
              <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
                <button className="btn-ghost btn-sm" onClick={() => setTab('appel')}>☰ Feuilles d'appel</button>
                <button className="btn btn-sm" onClick={() => setAddModal({})}>+ Saisir une absence</button>
              </div>
            </div>
          </div>
        </>
      )}

      {tab === 'appel' && presenceMode === 'DEMI_JOURNEE' && (
        <AppelDemiJournee classes={classes} classId={classId} setClassId={setClassId} date={date} setDate={setDate} />
      )}
      {tab === 'appel' && presenceMode === 'PAR_COURS' && (
        <div className="card">
          <div className="card-head">
            <div>
              <h2>Feuille d'appel</h2>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
                <span style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>{dateLabel}</span>
                <span className={`badge ${presenceMode === 'DEMI_JOURNEE' ? 'gold' : 'blue'}`} style={{ fontSize: 10 }}>
                  {presenceMode === 'DEMI_JOURNEE' ? '🌅 Demi-journée' : '📚 Par cours'}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select value={classId} onChange={(e) => setClassId(e.target.value)} style={{ width: 160 }}>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
<DateNav value={date} onChange={setDate} />
            </div>
          </div>
          <div className="card-body">
            {error && <div className="form-error">{error}</div>}

            {/* Compteurs */}
            <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 12 }}>
              <div className="kpi green"><span>Présents</span><strong>{counts.PRESENT}</strong></div>
              <div className="kpi"><span>Absents</span><strong style={{ color: counts.ABSENT > 0 ? 'var(--danger)' : 'inherit' }}>{counts.ABSENT}</strong></div>
              <div className="kpi gold"><span>Retards</span><strong>{counts.RETARD}</strong></div>
              <div className="kpi"><span>Excusés</span><strong>{counts.EXCUSE}</strong></div>
            </div>

            {saved && <div className="receipt-banner">Appel enregistré ✓</div>}
            {notifyResult && <div className="receipt-banner">Notifications envoyées : {notifyResult.sent} / {notifyResult.total}</div>}

            <div className="table-wrap"><table>
              <thead>
                <tr>
                  <th>Matricule</th><th>Élève</th>
                  {STATUSES.map((s) => <th key={s.v} style={{ textAlign: 'center', width: 60 }}>{s.title}</th>)}
                  <th>Motif / Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.studentId} style={r.status === 'ABSENT' ? { background: 'var(--danger-soft, #fff5f5)' } : r.status === 'RETARD' ? { background: '#fffde7' } : {}}>
                    <td className="mono">{r.matricule}</td>
                    <td><b>{r.name}</b></td>
                    {STATUSES.map((s) => (
                      <td key={s.v} style={{ textAlign: 'center' }}>
                        <button
                          onClick={() => setStatus(r.studentId, s.v)}
                          style={{
                            width: 32, height: 32, borderRadius: '50%', border: '2px solid',
                            fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
                            background: r.status === s.v ? (s.v === 'PRESENT' ? '#4caf50' : s.v === 'ABSENT' ? '#f44336' : s.v === 'RETARD' ? '#ff9800' : '#2196f3') : 'transparent',
                            borderColor: s.v === 'PRESENT' ? '#4caf50' : s.v === 'ABSENT' ? '#f44336' : s.v === 'RETARD' ? '#ff9800' : '#2196f3',
                            color: r.status === s.v ? 'white' : (s.v === 'PRESENT' ? '#4caf50' : s.v === 'ABSENT' ? '#f44336' : s.v === 'RETARD' ? '#ff9800' : '#2196f3'),
                          }}
                        >{s.l}</button>
                      </td>
                    ))}
                    <td>
                      <select value={r.note || ''} onChange={(e) => { setSaved(false); setRows((rs) => rs.map((row) => row.studentId === r.studentId ? { ...row, note: e.target.value } : row)); }}
                        style={{ fontSize: '0.78rem', width: '100%' }} disabled={r.status === 'PRESENT'}>
                        <option value="">—</option>
                        {MOTIFS.map((m) => <option key={m}>{m}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>

            {rows.length === 0 && <div className="empty">Aucun élève dans cette classe.</div>}

            <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
              <button className="btn-ghost btn-sm" onClick={allPresent}>✓ Tout présent</button>
              <button className="btn" onClick={save} disabled={saving || !rows.length} style={{ width: 'auto', padding: '0 22px' }}>
                {saving ? 'Enregistrement…' : 'Enregistrer l\'appel'}
              </button>
              {counts.ABSENT > 0 && (
                <button className="btn-ghost btn-sm" onClick={notifyAbsents}>📱 Notifier les parents des absents</button>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'historique' && <HistoriqueAttendance classes={classes} />}

      {addModal && <AbsenceModal initial={addModal} classes={classes} students={[]} onClose={() => setAddModal(null)} onSaved={() => { setAddModal(null); loadDashboard(); }} />}
    </>
  );
}

function HistoriqueAttendance({ classes }) {
  const [classId, setClassId] = useState(classes[0]?.id || '');
  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); });
  const [to, setTo] = useState(today());
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => { if (classes[0]) setClassId(classes[0].id); }, [classes]);

  function load() {
    if (!classId) return;
    api.get('/registry/presences-eleves').then((d) => {
      const all = (Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data }));
      const filtered = all.filter((r) =>
        (!classId || r.classId === classId) && r.date >= from && r.date <= to
      );
      // Aplatir les entries
      const flat = filtered.flatMap((r) =>
        (r.entries || []).map((e) => ({ ...e, date: r.date, classId: r.classId }))
      );
      setRows(flat);
    }).catch((e) => setError(e.message));
  }
  useEffect(load, [classId, from, to]);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Historique des présences</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={classId} onChange={(e) => setClassId(e.target.value)} style={{ width: 160 }}>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>
      <div className="card-body">
        {error && <div className="form-error">{error}</div>}
        {rows.length === 0 ? <div className="empty">Aucun enregistrement.</div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Date</th><th>Élève</th><th>Matricule</th><th>Statut</th><th>Motif</th></tr></thead>
            <tbody>{rows.map((r, i) => (
              <tr key={i}>
                <td className="mono">{new Date(r.date).toLocaleDateString('fr-FR')}</td>
                <td><b>{r.lastName} {r.firstName}</b></td>
                <td className="mono">{r.matricule}</td>
                <td><span className={`badge ${r.status === 'PRESENT' ? 'green' : r.status === 'ABSENT' ? 'red' : r.status === 'RETARD' ? 'gold' : 'blue'}`}>{r.status}</span></td>
                <td>{r.note || '—'}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════
// Appel en demi-journée (Pré-Scolaire & Élémentaire)
// ══════════════════════════════════════════════════════
function AppelDemiJournee({ classes, classId, setClassId, date, setDate }) {
  const [students, setStudents] = useState([]);
  const [matin, setMatin]       = useState({});
  const [apresmidi, setApresmidi] = useState({});
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState('');

  const MOTIFS = ['Maladie', 'Rendez-vous médical', 'Raison familiale', 'Non justifié', 'Autre'];
  const STATUSES = [
    { v: 'PRESENT', l: 'P', color: '#16a34a' },
    { v: 'ABSENT',  l: 'A', color: '#dc2626' },
    { v: 'RETARD',  l: 'R', color: '#f59e0b' },
    { v: 'EXCUSE',  l: 'E', color: '#3b82f6' },
  ];

  useEffect(() => {
    if (!classId) return;
    api.get('/students').then((all) => {
      const inClass = (all || []).filter((s) =>
        (s.enrollments || []).some((en) => en.classId === classId && en.status === 'ACTIVE')
      );
      setStudents(inClass);
      // Init présences
      const initM = {}, initA = {};
      inClass.forEach((s) => {
        initM[s.id] = { status: 'PRESENT', motif: '' };
        initA[s.id] = { status: 'PRESENT', motif: '' };
      });
      setMatin(initM);
      setApresmidi(initA);
    }).catch(() => {});
  }, [classId, date]);

  function setStatus(session, studentId, status) {
    const setter = session === 'matin' ? setMatin : setApresmidi;
    setter((prev) => ({ ...prev, [studentId]: { ...prev[studentId], status } }));
    setSaved(false);
  }

  function setMotif(session, studentId, motif) {
    const setter = session === 'matin' ? setMatin : setApresmidi;
    setter((prev) => ({ ...prev, [studentId]: { ...prev[studentId], motif } }));
    setSaved(false);
  }

  function allPresent(session) {
    const all = {};
    students.forEach((s) => { all[s.id] = { status: 'PRESENT', motif: '' }; });
    if (session === 'matin') setMatin(all);
    else setApresmidi(all);
    setSaved(false);
  }

  const countMatin   = (st) => students.filter((s) => matin[s.id]?.status === st).length;
  const countApresmidi = (st) => students.filter((s) => apresmidi[s.id]?.status === st).length;

  async function save() {
    setSaving(true); setError('');
    try {
      // Sauvegarder matin et après-midi séparément
      const entries = students.map((s) => ({
        studentId: s.id,
        matin: matin[s.id]?.status || 'PRESENT',
        apresmidi: apresmidi[s.id]?.status || 'PRESENT',
        motifMatin: matin[s.id]?.motif || '',
        motifApresmidi: apresmidi[s.id]?.motif || '',
      }));
      await api.post('/registry/presences-demi-journee', {
        data: { classId, date, entries, mode: 'DEMI_JOURNEE' },
        label: `Appel demi-journée ${date}`,
        searchText: `${classId} ${date}`,
        refDate: date,
      });
      setSaved(true);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  const dateLabel = new Date(date + 'T12:00:00').toLocaleDateString('fr-FR', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
  });

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h2>Feuille d'appel — Demi-journée</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
            <span style={{ color: 'var(--muted)', fontSize: '0.82rem', textTransform: 'capitalize' }}>{dateLabel}</span>
            <span className="badge gold" style={{ fontSize: 10 }}>🌅 Pré-Sco / Élémentaire</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={classId} onChange={(e) => setClassId(e.target.value)} style={{ width: 150 }}>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          {saved && <span className="badge green">Enregistré ✓</span>}
          <button className="btn" onClick={save} disabled={saving || !students.length}
            style={{ width: 'auto', padding: '0 18px', height: 38 }}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="form-error">{error}</div>}

        {/* Compteurs */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          {/* Matin */}
          <div style={{ border: '0.5px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ background: '#fef3c7', padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>🌅 Matin</span>
              <div style={{ display: 'flex', gap: 10, fontSize: 12 }}>
                <span style={{ color: '#16a34a', fontWeight: 700 }}>P: {countMatin('PRESENT')}</span>
                <span style={{ color: '#dc2626', fontWeight: 700 }}>A: {countMatin('ABSENT')}</span>
                <span style={{ color: '#f59e0b', fontWeight: 700 }}>R: {countMatin('RETARD')}</span>
              </div>
              <button className="btn-ghost btn-sm" onClick={() => allPresent('matin')} style={{ fontSize: 11 }}>✓ Tout présent</button>
            </div>
          </div>
          {/* Après-midi */}
          <div style={{ border: '0.5px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ background: '#eff6ff', padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>🌆 Après-midi</span>
              <div style={{ display: 'flex', gap: 10, fontSize: 12 }}>
                <span style={{ color: '#16a34a', fontWeight: 700 }}>P: {countApresmidi('PRESENT')}</span>
                <span style={{ color: '#dc2626', fontWeight: 700 }}>A: {countApresmidi('ABSENT')}</span>
                <span style={{ color: '#f59e0b', fontWeight: 700 }}>R: {countApresmidi('RETARD')}</span>
              </div>
              <button className="btn-ghost btn-sm" onClick={() => allPresent('apresmidi')} style={{ fontSize: 11 }}>✓ Tout présent</button>
            </div>
          </div>
        </div>

        {/* Table */}
        {students.length === 0 ? <div className="empty">Aucun élève dans cette classe.</div> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Matricule</th>
                  <th>Élève</th>
                  {/* Matin */}
                  <th colSpan={4} style={{ textAlign: 'center', background: '#fffbeb', color: '#d97706' }}>
                    🌅 Matin
                  </th>
                  <th style={{ background: '#fffbeb', width: 120 }}>Motif matin</th>
                  {/* Après-midi */}
                  <th colSpan={4} style={{ textAlign: 'center', background: '#eff6ff', color: '#1d4ed8' }}>
                    🌆 Après-midi
                  </th>
                  <th style={{ background: '#eff6ff', width: 120 }}>Motif A-M</th>
                </tr>
                <tr>
                  <th /><th />
                  {STATUSES.map((s) => (
                    <th key={`m-${s.v}`} style={{ textAlign: 'center', width: 36, fontSize: 11, color: s.color, background: '#fffbeb' }}>{s.l}</th>
                  ))}
                  <th style={{ background: '#fffbeb' }} />
                  {STATUSES.map((s) => (
                    <th key={`a-${s.v}`} style={{ textAlign: 'center', width: 36, fontSize: 11, color: s.color, background: '#eff6ff' }}>{s.l}</th>
                  ))}
                  <th style={{ background: '#eff6ff' }} />
                </tr>
              </thead>
              <tbody>
                {students.map((s) => {
                  const sm = matin[s.id] || { status: 'PRESENT', motif: '' };
                  const sa = apresmidi[s.id] || { status: 'PRESENT', motif: '' };
                  const rowBg = sm.status === 'ABSENT' && sa.status === 'ABSENT'
                    ? 'var(--danger-soft)'
                    : sm.status === 'ABSENT' || sa.status === 'ABSENT'
                      ? '#fff8f0' : undefined;
                  return (
                    <tr key={s.id} style={{ background: rowBg }}>
                      <td className="mono">{s.matricule}</td>
                      <td><b>{s.lastName} {s.firstName}</b></td>
                      {/* Boutons matin */}
                      {STATUSES.map((st) => (
                        <td key={`m-${st.v}`} style={{ textAlign: 'center', padding: '4px 2px' }}>
                          <button onClick={() => setStatus('matin', s.id, st.v)} style={{
                            width: 30, height: 28, borderRadius: 6,
                            border: `1.5px solid ${st.color}`,
                            background: sm.status === st.v ? st.color : 'transparent',
                            color: sm.status === st.v ? '#fff' : st.color,
                            cursor: 'pointer', fontWeight: 700, fontSize: 11,
                          }}>{st.l}</button>
                        </td>
                      ))}
                      <td>
                        {sm.status !== 'PRESENT' && (
                          <select value={sm.motif} onChange={(e) => setMotif('matin', s.id, e.target.value)}
                            style={{ fontSize: 11, width: '100%' }}>
                            <option value="">—</option>
                            {MOTIFS.map((m) => <option key={m}>{m}</option>)}
                          </select>
                        )}
                      </td>
                      {/* Boutons après-midi */}
                      {STATUSES.map((st) => (
                        <td key={`a-${st.v}`} style={{ textAlign: 'center', padding: '4px 2px' }}>
                          <button onClick={() => setStatus('apresmidi', s.id, st.v)} style={{
                            width: 30, height: 28, borderRadius: 6,
                            border: `1.5px solid ${st.color}`,
                            background: sa.status === st.v ? st.color : 'transparent',
                            color: sa.status === st.v ? '#fff' : st.color,
                            cursor: 'pointer', fontWeight: 700, fontSize: 11,
                          }}>{st.l}</button>
                        </td>
                      ))}
                      <td>
                        {sa.status !== 'PRESENT' && (
                          <select value={sa.motif} onChange={(e) => setMotif('apresmidi', s.id, e.target.value)}
                            style={{ fontSize: 11, width: '100%' }}>
                            <option value="">—</option>
                            {MOTIFS.map((m) => <option key={m}>{m}</option>)}
                          </select>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function AbsenceModal({ initial, classes, onClose, onSaved }) {
  const [students, setStudents] = useState([]);
  const [f, setF] = useState({
    studentId: initial.studentId || '',
    classId: initial.classId || '',
    date: initial.date || today(),
    status: 'ABSENT',
    note: initial.note || '',
    commentaire: initial.commentaire || '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    api.get('/students').then(setStudents).catch(() => {});
  }, []);

  async function save() {
    if (!f.studentId || !f.date) { setError('Élève et date sont obligatoires.'); return; }
    setSaving(true);
    try {
      // Utiliser l'endpoint bulk avec un seul élève
      await api.post('/students/attendance/bulk', {
        classId: f.classId || undefined,
        date: f.date,
        entries: [{ studentId: f.studentId, status: f.status, note: f.note }]
      });
      onSaved();
    } catch (e) {
      // Fallback: enregistrer via registry
      try {
        await api.post('/registry/schoolLife', {
          data: { ...f, type: 'Absence', eventDate: f.date, studentName: students.find((s) => s.id === f.studentId) ? `${students.find((s) => s.id === f.studentId).lastName} ${students.find((s) => s.id === f.studentId).firstName}` : '' },
          label: `Absence - ${f.date}`,
          searchText: f.date,
          refDate: f.date,
        });
        onSaved();
      } catch (e2) { setError(e2.message); }
    } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Saisir une absence</h2>
        {error && <div className="form-error">{error}</div>}
        <div className="field"><label>Élève *</label>
          <select value={f.studentId} onChange={(e) => {
            const id = e.target.value;
            const s = students.find((s) => s.id === id);
            const classId = s?.enrollments?.[0]?.classId || s?.enrollments?.[0]?.class?.id || f.classId;
            const className = s?.enrollments?.[0]?.class?.name || '';
            setF((p) => ({ ...p, studentId: id, classId, className }));
          }}>
            <option value="">Sélectionner…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.lastName} {s.firstName} ({s.matricule})</option>)}
          </select>
        </div>
        <div className="field"><label>Classe <small style={{ color: 'var(--muted)' }}>(auto)</small></label>
          {f.className ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input value={f.className} readOnly style={{ flex: 1, background: 'var(--surface-soft, #f0f9f0)', fontWeight: 600, color: 'var(--success, #2e7d32)', cursor: 'default' }} />
              <button type="button" className="btn-ghost btn-sm" onClick={() => setF((p) => ({ ...p, classId: '', className: '' }))}>✕</button>
            </div>
          ) : (
            <select value={f.classId} onChange={set('classId')}>
              <option value="">—</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field"><label>Date *</label><input type="date" value={f.date} onChange={set('date')} /></div>
          <div className="field"><label>Statut</label>
            <select value={f.status} onChange={set('status')}>
              <option value="ABSENT">Absent</option>
              <option value="RETARD">Retard</option>
              <option value="EXCUSE">Excusé</option>
            </select>
          </div>
        </div>
        <div className="field"><label>Motif</label>
          <select value={f.note} onChange={set('note')}>
            <option value="">—</option>
            {['Maladie', 'Rendez-vous médical', 'Deuil', 'Convocation', 'Raison familiale', 'Transport', 'Non justifié', 'Autre'].map((m) => <option key={m}>{m}</option>)}
          </select>
        </div>
        <div className="field"><label>Commentaire</label>
          <textarea value={f.commentaire} onChange={set('commentaire')} rows={2} />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}
